import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { Search, Lock, Unlock, KeyRound, Copy, Check, AlertCircle, UserPlus, Trash2, Download, X } from 'lucide-react';
import { api, ApiError } from '@/api/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { StatusPill } from '@/components/ui/status-pill';
import { FilterChip } from '@/components/ui/filter-chip';
import { AdminPageHeader } from '@/components/admin/AdminPageHeader';
import { DataTable, type ColumnDef, type RowSelectionState } from '@/components/ui/data-table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import type {
  AdminUser,
  AdminModuleAdminUsersResponse,
  AdminModuleAdminRole,
  AdminModuleTemporaryPasswordResponse,
} from '@/api/generated/types.gen';

export const Route = createFileRoute('/admin/users')({
  component: AdminUsers,
});

function AdminUsers() {
  const [q, setQ] = useState('');
  const [roleFilter, setRoleFilter] = useState<'all' | string>('all');
  const [lockedOnly, setLockedOnly] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  // Build the URL once per filter combination so React Query keys stay stable.
  const queryString = useMemo(() => {
    const p = new URLSearchParams({ pageNumber: '1', pageSize: '50' });
    if (q.trim()) p.set('q', q.trim());
    if (roleFilter !== 'all') p.set('role', roleFilter);
    if (lockedOnly) p.set('lockedOnly', 'true');
    return p.toString();
  }, [q, roleFilter, lockedOnly]);

  const users = useQuery({
    queryKey: ['admin', 'users', queryString],
    queryFn: () => api<AdminModuleAdminUsersResponse>(`/admin/users?${queryString}`),
    retry: false,
  });

  const roles = useQuery({
    queryKey: ['admin', 'roles'],
    queryFn: () => api<AdminModuleAdminRole[]>('/admin/roles'),
    staleTime: 5 * 60_000,
    retry: false,
  });

  const userList: AdminUser[] = useMemo(() => users.data?.users ?? [], [users.data]);
  const selected = userList.find((u) => u.id === selectedId) ?? null;

  useEffect(() => {
    setRowSelection({});
  }, [queryString]);

  const columns = useMemo<ColumnDef<AdminUser>[]>(
    () => [
      {
        id: 'name',
        accessorFn: (u) => fullName(u),
        header: 'Name',
        cell: ({ row }) => (
          <span className="font-medium text-(--color-ink)">{fullName(row.original)}</span>
        ),
      },
      {
        accessorKey: 'email',
        header: 'Email',
        cell: ({ row }) => (
          <span className="font-mono text-[13px] text-(--color-ink-2)">{row.original.email ?? ''}</span>
        ),
      },
      {
        id: 'roles',
        header: 'Roles',
        enableSorting: false,
        cell: ({ row }) => {
          const u = row.original;
          return (u.roles?.length ?? 0) === 0 ? (
            <span className="text-[12px] text-(--color-ink-muted)">none</span>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {u.roles!.map((r) => (
                <StatusPill key={r} tone={r === 'Administrator' ? 'navy' : 'neutral'}>
                  {r}
                </StatusPill>
              ))}
            </div>
          );
        },
      },
      {
        id: 'status',
        header: 'Status',
        enableSorting: false,
        cell: ({ row }) =>
          row.original.isLocked ? (
            <StatusPill tone="danger" icon={<Lock className="size-3" />}>
              Locked
            </StatusPill>
          ) : (
            <span className="text-[12px] text-(--color-ink-muted)">Active</span>
          ),
      },
      {
        accessorKey: 'created',
        header: 'Created',
        cell: ({ row }) => (
          <span className="font-mono text-[13px] tabular-nums text-(--color-ink-muted)">
            {row.original.created ? new Date(row.original.created).toLocaleDateString('en-AU') : '—'}
          </span>
        ),
      },
    ],
    [],
  );

  const selectedCount = Object.keys(rowSelection).length;
  const exportCsv = () => {
    const rows = userList.filter((u) => rowSelection[u.id ?? '']);
    const escape = (v: string) => `"${v.replace(/"/g, '""')}"`;
    const lines = [['Name', 'Email', 'Roles', 'Status', 'Created'].join(',')];
    for (const u of rows) {
      lines.push(
        [
          fullName(u),
          u.email ?? '',
          (u.roles ?? []).join(' / '),
          u.isLocked ? 'Locked' : 'Active',
          u.created ? new Date(u.created).toLocaleDateString('en-AU') : '',
        ]
          .map((v) => escape(String(v)))
          .join(','),
      );
    }
    const blob = new Blob([lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `users-${rows.length}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <AdminPageHeader
        eyebrow="Reviewer console"
        title="Users"
        description="All registered taxpayers and practice staff. Click a row to manage roles, lock the account, or reset the password."
      />

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-60 flex-1">
          <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-(--color-ink-muted)" />
          <Input
            placeholder="Search by name or email…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-10"
          />
        </div>
        <div className="flex flex-wrap gap-1.5">
          <FilterChip label="All" active={roleFilter === 'all'} onClick={() => setRoleFilter('all')} />
          {(roles.data ?? []).map((r) => (
            <FilterChip
              key={r.id ?? r.name ?? ''}
              label={r.name ?? ''}
              active={roleFilter === (r.name ?? '')}
              onClick={() => setRoleFilter(r.name ?? 'all')}
            />
          ))}
          <FilterChip
            label="Locked only"
            active={lockedOnly}
            onClick={() => setLockedOnly((v) => !v)}
          />
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <UserPlus className="size-4" />
          Create user
        </Button>
      </div>

      {selectedCount > 0 && (
        <div className="flex items-center justify-between gap-3 rounded-(--radius) bg-(--color-navy-soft) px-4 py-2.5">
          <span className="text-[13px] font-semibold text-(--color-navy)">{selectedCount} selected</span>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="secondary" onClick={exportCsv}>
              <Download className="size-3.5" />
              Export CSV
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setRowSelection({})}>
              <X className="size-3.5" />
              Clear
            </Button>
          </div>
        </div>
      )}

      <DataTable
        columns={columns}
        data={userList}
        isLoading={users.isLoading}
        loadingMessage="Loading users…"
        emptyMessage={users.isError ? 'Could not load users (admin role required).' : 'No users match.'}
        getRowId={(u) => u.id ?? ''}
        enableRowSelection
        rowSelection={rowSelection}
        onRowSelectionChange={setRowSelection}
        onRowClick={(u) => setSelectedId(u.id ?? null)}
      />

      <UserDetailPanel
        user={selected}
        availableRoles={roles.data ?? []}
        onClose={() => setSelectedId(null)}
      />

      <CreateUserDialog
        open={createOpen}
        availableRoles={roles.data ?? []}
        onClose={() => setCreateOpen(false)}
      />
    </div>
  );
}

function CreateUserDialog({
  open,
  availableRoles,
  onClose,
}: {
  open: boolean;
  availableRoles: AdminModuleAdminRole[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [tempPassword, setTempPassword] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setEmail('');
      setRole('');
      setError(null);
      setTempPassword(null);
    }
  }, [open]);

  const create = useMutation({
    mutationFn: () =>
      api<{ userId: string; temporaryPassword: string }>('/admin/users', {
        method: 'POST',
        json: { email: email.trim(), role: role || null },
      }),
    onError: (e: unknown) => setError(extractError(e)),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['admin', 'users'] });
      setTempPassword(data.temporaryPassword);
    },
  });

  return (
    <>
      <Dialog open={open && !tempPassword} onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="p-7">
          <DialogTitle>Create user</DialogTitle>
          <DialogDescription className="mt-1.5">
            A temporary password is generated for you to share. The user should change it after
            signing in.
          </DialogDescription>

          <form
            className="mt-5 space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              if (email.trim()) create.mutate();
            }}
          >
            <div className="space-y-1.5">
              <label className="text-[12px] font-semibold uppercase tracking-[0.14em] text-(--color-ink-muted)">
                Email
              </label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoFocus />
            </div>
            <div className="space-y-1.5">
              <label className="text-[12px] font-semibold uppercase tracking-[0.14em] text-(--color-ink-muted)">
                Role (optional)
              </label>
              <Select value={role} onChange={(e) => setRole(e.target.value)}>
                <option value="">No role</option>
                {availableRoles.map((r) => (
                  <option key={r.id ?? r.name ?? ''} value={r.name ?? ''}>
                    {r.name}
                  </option>
                ))}
              </Select>
            </div>

            {error && (
              <div className="flex items-start gap-2 text-[13px] text-(--color-maroon)">
                <AlertCircle className="mt-0.5 size-4 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" variant="ghost" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={!email.trim() || create.isPending}>
                {create.isPending ? 'Creating…' : 'Create user'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <TempPasswordModal
        password={tempPassword}
        onDone={() => {
          setTempPassword(null);
          onClose();
        }}
      />
    </>
  );
}

function UserDetailPanel({
  user,
  availableRoles,
  onClose,
}: {
  user: AdminUser | null;
  availableRoles: AdminModuleAdminRole[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const open = user !== null;

  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [tempPassword, setTempPassword] = useState<string | null>(null);
  const [confirmLock, setConfirmLock] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset local state every time the panel opens with a different user.
  useEffect(() => {
    if (user) {
      setSelectedRoles(user.roles ?? []);
      setTempPassword(null);
      setConfirmLock(false);
      setConfirmDelete(false);
      setError(null);
    }
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const invalidateUsers = () =>
    qc.invalidateQueries({ queryKey: ['admin', 'users'] });

  const updateRoles = useMutation({
    mutationFn: (roles: string[]) =>
      api(`/admin/users/${user!.id}/roles`, { method: 'POST', json: { roles } }),
    onError: (e: unknown) => setError(extractError(e)),
    onSuccess: invalidateUsers,
  });

  const lock = useMutation({
    mutationFn: () => api(`/admin/users/${user!.id}/lock`, { method: 'POST' }),
    onError: (e: unknown) => setError(extractError(e)),
    onSuccess: () => {
      setConfirmLock(false);
      invalidateUsers();
    },
  });

  const unlock = useMutation({
    mutationFn: () => api(`/admin/users/${user!.id}/unlock`, { method: 'POST' }),
    onError: (e: unknown) => setError(extractError(e)),
    onSuccess: invalidateUsers,
  });

  const resetPassword = useMutation({
    mutationFn: () =>
      api<AdminModuleTemporaryPasswordResponse>(
        `/admin/users/${user!.id}/reset-password`,
        { method: 'POST' },
      ),
    onError: (e: unknown) => setError(extractError(e)),
    onSuccess: (data) => setTempPassword(data.temporaryPassword ?? null),
  });

  const deleteUser = useMutation({
    mutationFn: () => api(`/admin/users/${user!.id}`, { method: 'DELETE' }),
    onError: (e: unknown) => setError(extractError(e)),
    onSuccess: () => {
      invalidateUsers();
      onClose();
    },
  });

  if (!user) return null;

  const currentRoles = user.roles ?? [];
  const rolesDirty =
    selectedRoles.length !== currentRoles.length ||
    selectedRoles.some((r) => !currentRoles.includes(r));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent variant="side" className="flex flex-col">
        <div className="border-b border-(--color-rule) px-7 py-6">
          <div className="section-ref">Account</div>
          <DialogTitle className="mt-1">{fullName(user)}</DialogTitle>
          <DialogDescription className="mt-1 font-mono text-[12px]">{user.email ?? ''}</DialogDescription>
        </div>

        <div className="flex-1 overflow-y-auto px-7 py-6 space-y-7 scrollbar-thin">
          {error && (
            <div className="flex items-start gap-2 rounded-(--radius) border border-(--color-maroon) bg-(--color-maroon-soft) px-3 py-2 text-[13px] text-(--color-maroon)">
              <AlertCircle className="mt-0.5 size-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <section>
            <div className="section-ref">Metadata</div>
            <dl className="mt-2 divide-y divide-(--color-rule) border-t border-(--color-rule) text-[13px]">
              <Row k="Created" v={user.created ? new Date(user.created).toLocaleString('en-AU') : '—'} />
              <Row
                k="Status"
                v={user.isLocked ? `Locked until ${formatLockoutEnd(user.lockoutEnd ?? null)}` : 'Active'}
              />
            </dl>
          </section>

          <section>
            <div className="section-ref">Roles</div>
            {availableRoles.length === 0 ? (
              <p className="mt-2 text-[12px] text-(--color-ink-muted)">No roles defined.</p>
            ) : (
              <div className="mt-2 space-y-2">
                {availableRoles.map((r) => {
                  const name = r.name ?? '';
                  const checked = selectedRoles.includes(name);
                  return (
                    <label
                      key={r.id ?? name}
                      className="flex cursor-pointer items-center gap-2.5 text-[13px] text-(--color-ink)"
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() =>
                          setSelectedRoles((prev) =>
                            checked ? prev.filter((x) => x !== name) : [...prev, name],
                          )
                        }
                        className="size-4 accent-(--color-navy)"
                      />
                      {name}
                    </label>
                  );
                })}
                <Button
                  size="sm"
                  variant="default"
                  disabled={!rolesDirty || updateRoles.isPending}
                  onClick={() => updateRoles.mutate(selectedRoles)}
                  className="mt-3"
                >
                  {updateRoles.isPending ? 'Saving…' : 'Save roles'}
                </Button>
              </div>
            )}
          </section>

          <section>
            <div className="section-ref">Account actions</div>
            <div className="mt-3 space-y-2">
              {user.isLocked ? (
                <Button
                  size="md"
                  variant="secondary"
                  disabled={unlock.isPending}
                  onClick={() => unlock.mutate()}
                  className="w-full justify-start"
                >
                  <Unlock className="size-4" />
                  {unlock.isPending ? 'Unlocking…' : 'Unlock account'}
                </Button>
              ) : confirmLock ? (
                <div className="rounded-(--radius) border border-(--color-rule) bg-(--color-paper-2) p-3">
                  <p className="text-[13px] text-(--color-ink)">
                    Lock this account? The user will be unable to sign in until you unlock them.
                  </p>
                  <div className="mt-3 flex gap-2">
                    <Button
                      size="sm"
                      variant="danger"
                      disabled={lock.isPending}
                      onClick={() => lock.mutate()}
                    >
                      {lock.isPending ? 'Locking…' : 'Confirm lock'}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setConfirmLock(false)}>
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <Button
                  size="md"
                  variant="secondary"
                  onClick={() => setConfirmLock(true)}
                  className="w-full justify-start"
                >
                  <Lock className="size-4" />
                  Lock account
                </Button>
              )}

              <Button
                size="md"
                variant="secondary"
                disabled={resetPassword.isPending}
                onClick={() => resetPassword.mutate()}
                className="w-full justify-start"
              >
                <KeyRound className="size-4" />
                {resetPassword.isPending ? 'Generating…' : 'Reset password'}
              </Button>

              {confirmDelete ? (
                <div className="rounded-(--radius) border border-(--color-maroon) bg-(--color-maroon-soft) p-3">
                  <p className="text-[13px] text-(--color-ink)">
                    Permanently delete this user? This can't be undone.
                  </p>
                  <div className="mt-3 flex gap-2">
                    <Button
                      size="sm"
                      variant="danger"
                      disabled={deleteUser.isPending}
                      onClick={() => deleteUser.mutate()}
                    >
                      {deleteUser.isPending ? 'Deleting…' : 'Confirm delete'}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(false)}>
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <Button
                  size="md"
                  variant="secondary"
                  onClick={() => setConfirmDelete(true)}
                  className="w-full justify-start text-(--color-maroon)"
                >
                  <Trash2 className="size-4" />
                  Delete user
                </Button>
              )}
            </div>
          </section>
        </div>
      </DialogContent>

      <TempPasswordModal
        password={tempPassword}
        onDone={() => setTempPassword(null)}
      />
    </Dialog>
  );
}

function TempPasswordModal({
  password,
  onDone,
}: {
  password: string | null;
  onDone: () => void;
}) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setCopied(false);
  }, [password]);

  if (!password) return null;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(password);
      setCopied(true);
    } catch {
      // ignore — fallback is the visible text
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onDone()}>
      <DialogContent className="p-7">
        <div className="section-ref">One-time</div>
        <DialogTitle className="mt-1">Temporary password</DialogTitle>
        <DialogDescription className="mt-1.5">
          Share this with the user now. It is not stored and will not be shown again. They
          should change it immediately after signing in.
        </DialogDescription>
        <div className="mt-5 flex items-center justify-between gap-3 rounded-(--radius) border-2 border-(--color-navy) bg-(--color-navy-soft) px-4 py-3 font-mono text-lg tabular-nums text-(--color-navy)">
          <code>{password}</code>
          <button
            type="button"
            onClick={copy}
            className="inline-flex items-center gap-1 rounded-(--radius-sm) border border-(--color-navy)/40 bg-(--color-card) px-2 py-1 text-[12px] font-medium text-(--color-navy) hover:bg-(--color-paper-2)"
          >
            {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
        <div className="mt-6 flex justify-end">
          <Button onClick={onDone}>Done</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex items-start justify-between gap-4 py-3">
      <dt className="text-(--color-ink-muted)">{k}</dt>
      <dd className="text-right text-(--color-ink)">{v}</dd>
    </div>
  );
}

function fullName(u: AdminUser): string {
  const name = `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim();
  return name || (u.email ?? '');
}

function formatLockoutEnd(iso: string | null): string {
  if (!iso) return 'until unlocked';
  const d = new Date(iso);
  if (d.getFullYear() > 9000) return 'indefinitely';
  return d.toLocaleString('en-AU');
}

function extractError(e: unknown): string {
  if (e instanceof ApiError) {
    const p = e.problem as { errors?: string[]; title?: string } | null;
    if (p?.errors?.length) return p.errors.join(' · ');
    if (p?.title) return p.title;
    return `Request failed (HTTP ${e.status})`;
  }
  return 'Request failed';
}
